import React from 'react';

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ModeIcon from '@mui/icons-material/Mode';
import { Box, FormControlLabel, IconButton, Stack, Switch, TextField } from '@mui/material';

import {
  type PackageInclusionRecord,
  getInclusionCategory,
  getInclusionName,
  getInclusionPersistId,
  isInclusionHidden,
} from '../../../utils';

import { PACKAGE_INCLUSIONS_GRID_COLUMNS } from './utils';

interface Props {
  id: string;
  inclusion: PackageInclusionRecord;
  onChange: (nextInclusion: PackageInclusionRecord) => void;
  vendorId?: string;
}

export default function PackageInclusionsTabRow({ id, inclusion, onChange, vendorId }: Props) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const isHidden = isInclusionHidden(inclusion);
  const category = getInclusionCategory(inclusion);
  const inclusionId = getInclusionPersistId(inclusion);
  const editInclusionUrl =
    vendorId && inclusionId ? `/vendors/${vendorId}/inclusions?inclusionId=${inclusionId}` : undefined;

  return (
    <Box
      ref={setNodeRef}
      sx={{
        alignItems: 'center',
        borderTop: 1,
        borderColor: 'divider',
        display: 'grid',
        gap: 2,
        gridTemplateColumns: { xs: 'minmax(0, 1fr) minmax(0, 1fr) auto', lg: PACKAGE_INCLUSIONS_GRID_COLUMNS },
        opacity: isDragging ? 0.72 : isHidden ? 0.58 : 1,
        px: 2,
        py: 2,
        transform: CSS.Transform.toString(transform),
        transition,
      }}
    >
      <IconButton
        size="small"
        aria-label={`Reorder ${getInclusionName(inclusion) || 'inclusion'}`}
        sx={{
          color: 'text.disabled',
          cursor: isDragging ? 'grabbing' : 'grab',
          gridColumn: { xs: '1 / -1', lg: 'auto' },
          justifySelf: { xs: 'center', lg: 'auto' },
        }}
        {...attributes}
        {...listeners}
      >
        <DragIndicatorIcon fontSize="small" />
      </IconButton>

      <TextField
        value={category}
        size="small"
        fullWidth
        disabled={isHidden}
        sx={{ gridColumn: { xs: '1 / -1', lg: 'auto' } }}
        slotProps={{ input: { readOnly: true } }}
      />

      <TextField
        value={getInclusionName(inclusion)}
        size="small"
        fullWidth
        disabled={isHidden}
        sx={{ gridColumn: { xs: '1 / -1', lg: 'auto' } }}
        slotProps={{ input: { readOnly: true } }}
      />

      <Stack direction="row" justifyContent={{ xs: 'flex-start', lg: 'center' }} sx={{ minWidth: 0 }}>
        <FormControlLabel
          label="Highlight"
          control={
            <Switch
              checked={inclusion.style === 'highlighted'}
              onChange={(event) => onChange({ ...inclusion, style: event.target.checked ? 'highlighted' : null })}
              disabled={isHidden || !!inclusion.lux_plus_tier}
            />
          }
          sx={{
            m: 0,
            '& .MuiFormControlLabel-label': {
              color: 'text.secondary',
              display: { xs: 'block', lg: 'none' },
              fontSize: '0.875rem',
            },
          }}
        />
      </Stack>

      <Stack direction="row" justifyContent={{ xs: 'flex-start', lg: 'center' }} sx={{ minWidth: 0 }}>
        <FormControlLabel
          label="LuxPlus"
          control={
            <Switch
              checked={inclusion.lux_plus_tier === 'base'}
              onChange={(event) => onChange({ ...inclusion, lux_plus_tier: event.target.checked ? 'base' : null })}
              disabled={isHidden || inclusion.style === 'highlighted' || inclusion.style === 'bonus'}
            />
          }
          sx={{
            m: 0,
            '& .MuiFormControlLabel-label': {
              color: 'text.secondary',
              display: { xs: 'block', lg: 'none' },
              fontSize: '0.875rem',
            },
          }}
        />
      </Stack>

      <Stack
        direction="row"
        justifyContent={{ xs: 'flex-end', lg: 'center' }}
        spacing={0.5}
        sx={{ justifySelf: { xs: 'end', lg: 'auto' } }}
      >
        <IconButton
          color="primary"
          aria-label="Edit inclusion"
          href={editInclusionUrl}
          target="_blank"
          rel="noreferrer"
          disabled={!editInclusionUrl}
        >
          <ModeIcon />
        </IconButton>
      </Stack>
    </Box>
  );
}
